"use client";
import React from "react";
import { useEffect, useRef, useState } from "react";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import styles from "./property-images.module.scss";
import { TotalFullScreenImageViewed } from "../../investor/amplitude";
import { useParams, useSearchParams } from "react-router-dom";
import useContextHook from "../../../hooks/context/useContext";

export default function GridGallary({
  imagesArr,
  objPropertyDetails,
  setImageViewCount
}: any) {
  const { propertyId } = useParams();
  const [imgIndex, setImgIndex] = useState(-1);
  const [viewedImages, setViewedImages] = useState(new Set());
  const galleryRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [fullScreenImages, setFullScreenImages] = useState(new Set());
  const { mobileNumber } = useContextHook();
  const [searchParams] = useSearchParams();
  const cid = searchParams.get("cid") || "";
  const eq = searchParams.get("eq") || "";

  const isVideo = (url: string) => {
    return url?.match(/\.(mp4|mov|avi|wmv|flv|mkv)$/i) ? true : false;
  };

  const renderMedia = (media: any, index: number) => {
    const isVideoFile = isVideo(media?.url);

    if (isVideoFile) {
      return (
        <video
          key={index}
          className={`${styles.mediaContent} ${styles.GridGallaryVideo}`}
          autoPlay
          muted
          loop
          playsInline
          data-src={media.url}
        >
          <source src={media.url} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    }

    return (
      <img
        key={index}
        data-src={media.url}
        src={media.url}
        alt="images"
        className={`${styles.mediaContent} ${styles.GridGallaryVideo}`}
        onError={(e: any) => {
          e.target.src = "/images/default-property.jpg";
          e.target.alt = "Image not available";
        }}
      />
    );
  };

  const renderLightboxContent = () => {
    const currentMedia = imagesArr[imgIndex];
    const isVideoFile = isVideo(currentMedia?.url);

    if (isVideoFile) {
      return (
        <div
          key={imgIndex}
          className={styles.videoLightbox}
          onClick={(e) => {
            // Only close if clicking the container, not the video
            if (e.target === e.currentTarget) {
              closeLightbox();
            }
          }}
        >
          <button
            className={`${styles.navButton} ${styles.navLeft}`}
            onClick={(e) => {
              e.stopPropagation();
              handleMovePrev();
            }}
            style={{ left: "0" }}
          ></button>

          <video
            className={styles.videoLightboxContent}
            controls
            autoPlay
            playsInline
            onClick={(e) => e.stopPropagation()}
          >
            <source src={currentMedia.url} type="video/mp4" />
            Your browser does not support the video tag.
          </video>

          <button
            className={`${styles.navButton} ${styles.navRight}`}
            onClick={(e) => {
              e.stopPropagation();
              handleMoveNext();
            }}
            style={{ right: "0" }}
          ></button>

          <button
            className={styles.closeButton}
            onClick={(e) => {
              e.stopPropagation();
              closeLightbox();
            }}
          >
            <img src="/images/close-video.svg" alt="videolighning-close" />
          </button>
        </div>
      );
    }

    return (
      <Lightbox
        mainSrc={currentMedia?.url}
        nextSrc={imagesArr[(imgIndex + 1) % imagesArr.length]?.url}
        prevSrc={
          imagesArr[(imgIndex + imagesArr.length - 1) % imagesArr.length]?.url
        }
        onCloseRequest={closeLightbox}
        onMovePrevRequest={handleMovePrev}
        onMoveNextRequest={handleMoveNext}
        reactModalStyle={{
          overlay: {
            zIndex: 5000
          }
        }}
        animationDuration={300}
      />
    );
  };

  useEffect(() => {
    if (!galleryRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        let newViewedImages = new Set(viewedImages);
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const imgSrc = entry.target.getAttribute("data-src");
            if (imgSrc) {
              newViewedImages.add(imgSrc);
            }
          }
        });

        if (newViewedImages.size !== viewedImages.size) {
          setImageViewCount(+newViewedImages.size);
        }
      },
      { threshold: 0.5 }
    );

    const mediaElements = galleryRef.current.querySelectorAll("img, video");
    mediaElements.forEach((element) => observerRef.current?.observe(element));

    return () => observerRef.current?.disconnect();
  }, [viewedImages]);

  const openLightbox = (index: any) => {
    setImgIndex(index);
    trackFullScreenView(index);

    const newViewedImages = new Set(viewedImages);
    newViewedImages.add(imagesArr[index]?.url);
    setViewedImages(newViewedImages);
  };

  const closeLightbox = () => {
    // Track full screen views when closing the lightbox
    TotalFullScreenImageViewed({
      totalImagesViewed: fullScreenImages.size,
      propertyId: objPropertyDetails?.propertyId,
      opportunitySFID: objPropertyDetails?.opportunitySFID,
      pubPropertySFID: objPropertyDetails?.pubPropertySFID,
      propertySFID: objPropertyDetails?.propertySFID,
      shareId: propertyId || "",
      linkCId: cid,
      linkEmailId: eq,
      ...(mobileNumber && { mobileNumber: mobileNumber })
    });
    setImgIndex(-1);
  };
  const handleMovePrev = () => {
    const prevIndex = (imgIndex + imagesArr.length - 1) % imagesArr.length;
    setImgIndex(prevIndex);
    trackFullScreenView(prevIndex);
  };

  const handleMoveNext = () => {
    const nextIndex = (imgIndex + 1) % imagesArr.length;
    setImgIndex(nextIndex);
    trackFullScreenView(nextIndex);
  };

  const trackFullScreenView = (index: number) => {
    const newFullScreenImages = new Set(fullScreenImages);
    newFullScreenImages.add(imagesArr[index]?.url);

    if (newFullScreenImages.size !== fullScreenImages.size) {
      setFullScreenImages(newFullScreenImages);
    }
  };

  return (
    <div className={styles.gallareyDiv} ref={galleryRef}>
      <div className={styles.imagesDiv}>
        {imagesArr.map((media: any, index: any) => {
          const isSingleColumn = index % 3 === 0;
          const columnStyle = isSingleColumn
            ? styles.singleColumn
            : styles.twoColumn;
          return (
            <div
              key={index}
              onClick={() => openLightbox(index)}
              className={columnStyle}
            >
              {renderMedia(media, index)}
            </div>
          );
        })}
      </div>

      {imgIndex >= 0 && renderLightboxContent()}
    </div>
  );
}
